/**
 * Activity Log Store - tracks who changed what setting and when.
 * Stored in localStorage and synced to cloud via the sync layer.
 */

const LOG_KEY = "khays_activity_log";
const MAX_ENTRIES = 500;

export interface ActivityLogEntry {
  id: string;
  timestamp: number;
  username: string;
  action: string;       // e.g. "updated", "toggled", "created", "deleted"
  target: string;       // e.g. "Storefront Settings", "FAQ #3", "Flash Sale"
  details?: string;     // e.g. "Locked storefront", "Changed price to GH₵500"
}

export function loadActivityLog(): ActivityLogEntry[] {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

export function saveActivityLog(entries: ActivityLogEntry[]): void {
  localStorage.setItem(LOG_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
}

export function addActivityEntry(entry: Omit<ActivityLogEntry, "id" | "timestamp">): void {
  // Check if logging is enabled
  try {
    const enabled = localStorage.getItem("khays_activity_log_enabled");
    if (enabled === "false") return;
  } catch {}

  const log = loadActivityLog();
  log.push({
    id: `act_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    timestamp: Date.now(),
    ...entry,
  });
  saveActivityLog(log);
}

export function clearActivityLog(): void {
  localStorage.removeItem(LOG_KEY);
}

/** Friendly labels for settings keys */
export function settingKeyToLabel(key: string): string {
  const map: Record<string, string> = {
    khays_storefront_settings: "Storefront Settings",
    khays_admin_settings: "Admin Settings",
    khays_orders: "Orders",
    khays_stock: "Stock",
    khays_device_settings: "Device Settings",
    khays_about: "About Us",
    khays_reviews: "Reviews",
    khays_faqs: "FAQs",
    khays_flash_sale: "Flash Sale",
    khays_featured_config: "Featured Device",
    khays_shipping_options: "Shipping Options",
    khays_swap_settings: "Swap Settings",
    khays_swap_pricing: "Swap Pricing",
    khays_tracking_settings: "Tracking Settings",
    khays_tracking_presets: "Tracking Presets",
    khays_category_settings: "Category Settings",
    khays_media: "Media",
    khays_ad_slots: "Ad Slots",
    khays_enquiries: "Enquiries",
  };
  return map[key] || key.replace(/^khays_/, "").replace(/_/g, " ");
}
